/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

// This is a feedback form that enables user to give descriptive feedback(comment) on the current document or provide suggestions

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, { useEffect, useRef } from 'react';

import { snackNotifications } from '../../notification/SnackBarUtils';
import styles from './FeedbackForm.module.scss';
import { DocumentVersionType } from '../../algorithm/types';
import { DigitalAssistantFeedbackTypes, feedbackChoices } from './FeedbackType';
import { useAppSelector } from '../../../../app/hooks';
import { selectPatientSummaryFeedback } from './feedbackSlice';
import { useLocation } from 'react-router-dom';
import { selectCreatorMode } from '../chatSlice';
import ReactQuillEditor from '../ReactQuillEditor/ReactQuillEditor';
import { postFeedback } from './feedbackApiHelper';
import { uploadAllAttachments } from '../ReactQuillEditor/react-quill-utils';
import CircularProgress from '@mui/material/CircularProgress';
import { captureException as sentryCaptureException } from '@sentry/react';

export type FeedbackFormProps = {
  documentVersion?: DocumentVersionType;
  onClose: () => void;
  isSmallScreen?: boolean;
  messageIndex?: number;
};
export type FeedbackAttachment = {
  filename: string;
  mimetype: string;
  bytesize: number;
  signature: string;
  url?: string;
  tinyurl?: string;
};

export default function FeedbackForm({ documentVersion, onClose, isSmallScreen }: FeedbackFormProps) {
  const [content, setContent] = React.useState<string>('');
  const [saving, setSaving] = React.useState<boolean>(false);

  const defaultSelection = useAppSelector(selectPatientSummaryFeedback);
  const [digitalAssistantFeedbackType, setDigitalAssistantFeedbackType] =
    React.useState<DigitalAssistantFeedbackTypes | null>(defaultSelection || null);

  const feedbackRef = useRef<HTMLDivElement | null>(null);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const isNotChatPage = !useLocation().pathname.includes('chat');
  const [attachments, setAttachments] = React.useState<FeedbackAttachment[]>([]);
  const filesBySignature = useRef(new Map<string, File>());

  const handleFeedbackSubmit = async (e: React.BaseSyntheticEvent) => {
    try {
      e.preventDefault();
      setSaving(true);
      if (!content || !digitalAssistantFeedbackType) return;
      const contentWithoutImages = stripImagesFromHtml(content);
      const response =
        digitalAssistantFeedbackType === DigitalAssistantFeedbackTypes.patientSummary ||
        digitalAssistantFeedbackType === DigitalAssistantFeedbackTypes.currentTopic
          ? await postFeedback(contentWithoutImages, digitalAssistantFeedbackType, attachments, documentVersion?.id)
          : await postFeedback(contentWithoutImages, digitalAssistantFeedbackType, attachments);

      const responseAttachments = response?.data?.data?.attachments;
      if (responseAttachments?.length) {
        await uploadAllAttachments(responseAttachments, filesBySignature.current);
      }
      snackNotifications.success(
        `Thank You for your feedback. We're working to address your suggestions and make it even more user-friendly.`
      );
      onClose();
    } catch (error) {
      snackNotifications.error('An error occurred, unable to process your feedback currently');
      sentryCaptureException(error);
    } finally {
      setSaving(false);
    }
  };

  const stripImagesFromHtml = (html: string) => html.replace(/<img\b[^>]*>/gi, '');
  const MRN_TEST = /\b\d{8,11}\b/;
  const MRN_REPLACE = /\b\d{8,11}\b/g;
  const handleContentChange = (value: string) => {
    const html = typeof value === 'string' ? value : '';
    const root = document.createElement('div');
    root.innerHTML = html;
    let found = false;
    // Avoid processing images in content, this causes unnecessary errors.
    root.querySelectorAll('p,div').forEach((el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode())) {
        const t = node.nodeValue || '';
        if (MRN_TEST.test(t)) {
          found = true;
          node.nodeValue = t.replace(MRN_REPLACE, '');
        }
      }
    });
    if (found) {
      snackNotifications.error(
        'MRNs may not be added here. Patient data will only come from launching within the EMR.'
      );
    }
    setContent(root.innerHTML);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        feedbackRef.current &&
        !feedbackRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('#documentversion-feedback')
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  return (
    <div
      className={styles.digitalAssistantForm}
      data-testid="digitalAssistantForm"
      style={{ position: !!isSmallScreen ? 'relative' : 'fixed' }}
      ref={feedbackRef}
      data-isconsumer={!isCreatorMode && 'true'}
    >
      <form onSubmit={handleFeedbackSubmit}>
        <FormControl disabled={saving} className={styles.feedbackFormControl}>
          <RadioGroup
            aria-labelledby="feedback-choices"
            className={styles.radioGroup}
            name="feedback-choices-group"
            value={digitalAssistantFeedbackType}
            onChange={(event) => setDigitalAssistantFeedbackType(event.target.value as DigitalAssistantFeedbackTypes)}
          >
            {feedbackChoices.map((choice, index) => (
              <FormControlLabel
                value={choice.value}
                key={index}
                className="feedback"
                disabled={
                  choice.value === DigitalAssistantFeedbackTypes.currentTopic && (!documentVersion || isNotChatPage)
                }
                control={<Radio className={styles.radioBtn} />}
                data-testid={`choice-${index}`}
                disableTypography
                label={choice.label}
                labelPlacement="start"
              />
            ))}
          </RadioGroup>
        </FormControl>

        {digitalAssistantFeedbackType && (
          <>
            <ReactQuillEditor
              documentId={documentVersion?.id || ''}
              value={content}
              onChange={handleContentChange}
              disabled={saving}
              maxAttachments={2}
              currentAttachmentCount={attachments.length}
              onAttachmentAdded={(att, file, uploadId) => {
                if (attachments.length > 2) {
                  snackNotifications.error('Sorry, You can only upload 2 attachments.');
                  return false; // tell editor to remove the image
                }

                setAttachments((prev) => [...prev, att]);
                filesBySignature.current.set(att.signature, file);
                return true;
              }}
              feedbackType={digitalAssistantFeedbackType}
              hideToolbar={!isCreatorMode}
            />

            <div className={styles.buttons}>
              <Button className={styles.cancelButton} onClick={onClose} data-testid="cancelFeedback" disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                className={styles.submitButton}
                data-testid="submitFeedback"
                disabled={!content.trim() || saving}
              >
                {!saving ? 'Submit' : <CircularProgress />}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
